import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import ErrorBoundary from './ErrorBoundary';
import SEO from './SEO';
import PageSelectionControls from './PageSelectionControls';
import PageRemovalResults from './PageRemovalResults';
import PDFPagePreview from './PDFPagePreview';
import { PDFPageRemovalService } from '../services/pdfPageRemovalService';
import { PAGE_REMOVAL_TYPES, validateRemovalConfig, getRemovalDescription, formatFileSize } from '../utils/pdfPageUtils';
import { getPDFPageRemoverSchema, getBreadcrumbSchema } from '../utils/structuredData';
import analytics from '../services/analytics';
import './PDFPageRemover.css';
import '../App.css';

function PDFPageRemover() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const [removalConfig, setRemovalConfig] = useState({
    type: PAGE_REMOVAL_TYPES.RANGE,
    options: { startPage: 1, endPage: 1 }
  });
  const [processingResults, setProcessingResults] = useState([]);
  const [previewPages, setPreviewPages] = useState([]);
  const [isClearing, setIsClearing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const fileInputRef = useRef(null);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // File upload handling
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      await handleFileSelected(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleFileSelected = async (file) => {
    setSelectedFile(file);
    setProcessingResults([]);
    setPdfInfo(null);
    setPreviewPages([]);

    // Track file upload using standard analytics method
    analytics.trackFileUpload(file.type, file.size);

    // Load PDF info
    const loadResult = await PDFPageRemovalService.loadPDF(file);
    if (loadResult.success) {
      setPdfInfo(loadResult);

      // Generate initial preview
      const thumbnailResult = await PDFPageRemovalService.generatePageThumbnails(loadResult.pdfDoc);
      if (thumbnailResult.success) {
        setPreviewPages(thumbnailResult.thumbnails);
      }

      // Track successful PDF load with page count
      analytics.trackEvent('pdf_page_remover', 'file_uploaded', {
        file_size: file.size,
        page_count: loadResult.pageCount
      });
    } else {
      // Track PDF loading error
      analytics.trackError('pdf_load_error', loadResult.error);
      alert('Error loading PDF: ' + loadResult.error);
    }
  };

  const handleRemovalConfigChange = useCallback((newConfig) => {
    setRemovalConfig(newConfig);
  }, []);

  const handleProcessRemoval = async () => {
    if (!selectedFile || !pdfInfo) {
      alert('Please select a PDF file first');
      return;
    }

    if (!validateRemovalConfig(removalConfig, pdfInfo.pageCount)) {
      alert('Invalid removal configuration. Please check your settings.');
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingResults([]);

    try {
      const result = await PDFPageRemovalService.processRemoval(
        selectedFile,
        removalConfig,
        (progress, stage) => {
          setProcessingProgress(progress);
          setProcessingStage(stage);
        }
      );

      if (result.success) {
        setProcessingResults([{
          success: true,
          originalPages: result.originalPages,
          removedCount: result.removedCount || 0,
          keptCount: result.keptCount || (result.originalPages - (result.removedCount || 0)),
          fileName: result.fileName,
          fileSize: result.fileSize,
          removedPages: result.removedPages,
          keptPages: result.keptPages,
          description: getRemovalDescription(removalConfig, pdfInfo.pageCount)
        }]);

        // Track successful processing
        analytics.trackEvent('pdf_page_remover', 'processing_success', {
          removal_type: removalConfig.type,
          original_pages: result.originalPages,
          removed_pages: result.removedCount || 0,
          kept_pages: result.keptCount || (result.originalPages - (result.removedCount || 0))
        });

        // Track PDF download (file is automatically downloaded by the service)
        analytics.trackDownload('pdf');

        setProcessingStage('File downloaded successfully!');
      } else {
        setProcessingResults([{
          success: false,
          error: result.error,
          description: getRemovalDescription(removalConfig, pdfInfo.pageCount)
        }]);

        // Track processing error
        analytics.trackEvent('pdf_page_remover', 'processing_error', {
          error: result.error
        });
      }

    } catch (error) {
      console.error('Processing error:', error);
      setProcessingResults([{
        success: false,
        error: error.message,
        description: getRemovalDescription(removalConfig, pdfInfo.pageCount)
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearAll = useCallback(async () => {
    setIsClearing(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));

      // Clear all state
      setSelectedFile(null);
      setPdfInfo(null);
      setProcessingResults([]);
      setPreviewPages([]);
      setProcessingProgress(0);
      setProcessingStage('');
      setRemovalConfig({
        type: PAGE_REMOVAL_TYPES.RANGE,
        options: { startPage: 1, endPage: 1 }
      });

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Cleanup resources
      PDFPageRemovalService.cleanup();

      console.log('✅ PDF Page Remover cleared');

    } finally {
      setIsClearing(false);
    }
  }, []);

  const handleRetryProcessing = async () => {
    await handleProcessRemoval();
  };

  const canProcess = selectedFile && pdfInfo && !isProcessing &&
                    validateRemovalConfig(removalConfig, pdfInfo.pageCount);

  const failedCount = processingResults.filter(r => !r.success).length;

  // Breadcrumb data for PDF page remover
  const breadcrumbData = [
    { name: 'Home', url: '/' },
    { name: 'PDF Page Remover', url: '/#page-remover' }
  ];

  // Combined structured data for PDF page remover
  const combinedStructuredData = [
    getPDFPageRemoverSchema(),
    getBreadcrumbSchema(breadcrumbData)
  ];

  return (
    <div className="App">
      <SEO
        title="PDF Page Remover - Remove Pages from PDF Online Free"
        description="Remove specific pages from PDF documents instantly. Remove first page, last page, page ranges, or keep only selected pages. Fast, secure, and works entirely in your browser - no file upload required."
        keywords="PDF page remover, remove PDF pages, delete PDF pages, PDF page extractor, PDF editor, remove first page, remove last page, PDF page manipulation, PDF page deletion, extract PDF pages"
        url="/#page-remover"
        type="article"
        canonical={`${window.location.origin}/#page-remover`}
        structuredData={combinedStructuredData}
      />

      <div className="container">
        <div className="pdf-header" style={{ paddingTop: '60px' }}>
          <button
            className="btn back-btn"
            onClick={() => window.location.hash = 'home'}
            style={{
              position: 'absolute',
              top: '10px',
              left: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '8px 16px',
              fontSize: '14px',
              zIndex: 10
            }}
          >
            ← Back to Home
          </button>
          <h1>🗑️ PDF Page Remover</h1>
          <p>Remove specific pages from your PDF documents</p>
        </div>

        {/* File Upload Section */}
        <div className="card">
          <ErrorBoundary onReset={handleClearAll}>
            <div className="page-remover-upload-section">
              <div {...getRootProps()} className={`page-remover-dropzone ${isDragActive ? 'active' : ''}`}>
                <input {...getInputProps()} ref={fileInputRef} />
                <div className="page-remover-dropzone-content">
                  <div className="page-remover-upload-icon">📄</div>
                  {isDragActive ? (
                    <p>Drop your PDF file here...</p>
                  ) : (
                    <>
                      <p>Drag & drop a PDF file here, or click to select</p>
                      <button
                        type="button"
                        className="page-remover-btn page-remover-btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Select PDF File
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Single File Limitation Notice */}
              <div className="page-remover-upload-notice">
                <div className="page-remover-notice-content">
                  <span className="page-remover-notice-icon">ℹ️</span>
                  <div className="page-remover-notice-text">
                    <strong>Note:</strong> Process one PDF at a time. Uploading a new file will replace the current selection.
                  </div>
                </div>
              </div>

              {selectedFile && (
                <div className="page-remover-selected-file">
                  <div className="page-remover-file-card">
                    <div className="page-remover-file-icon">
                      <span className="page-remover-file-type">PDF</span>
                    </div>
                    <div className="page-remover-file-info">
                      <div className="page-remover-file-name">
                        <span className="page-remover-file-title">{selectedFile.name}</span>
                      </div>
                      <div className="page-remover-file-meta">
                        <div className="page-remover-meta-item">
                          <span className="page-remover-meta-label">Size</span>
                          <span className="page-remover-meta-value">{formatFileSize(selectedFile.size)}</span>
                        </div>
                        {pdfInfo && (
                          <div className="page-remover-meta-item">
                            <span className="page-remover-meta-label">Pages</span>
                            <span className="page-remover-meta-value">{pdfInfo.pageCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="page-remover-file-actions">
                      <button
                        className="page-remover-clear-btn"
                        onClick={() => {
                          analytics.trackButtonClick('clear_file', 'pdf_page_remover');
                          handleClearAll();
                        }}
                        disabled={isClearing}
                        title="Clear and select a different file"
                      >
                        {isClearing ? (
                          <>
                            <span className="page-remover-spinner">⟳</span>
                            Clearing...
                          </>
                        ) : (
                          <>
                            <span className="page-remover-clear-icon">✕</span>
                            Clear
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ErrorBoundary>
        </div>

        {/* PDF Preview Section */}
        {pdfInfo && previewPages.length > 0 && (
          <div className="card">
            <h2>PDF Preview</h2>
            <PDFPagePreview
              pages={previewPages}
              totalPages={pdfInfo.pageCount}
              removalConfig={removalConfig}
            />
          </div>
        )}

        {/* Page Selection Controls */}
        {pdfInfo && (
          <div className="card">
            <h2>Page Removal Options</h2>
            <PageSelectionControls
              totalPages={pdfInfo.pageCount}
              removalConfig={removalConfig}
              onConfigChange={handleRemovalConfigChange}
              disabled={isProcessing}
            />

            {/* Process Button */}
            <div className="control-buttons">
              <button
                className={`page-remover-btn ${!canProcess ? '' : 'page-remover-btn-danger'}`}
                onClick={() => {
                  analytics.trackButtonClick('remove_pages', 'pdf_page_remover');
                  handleProcessRemoval();
                }}
                disabled={!canProcess}
                style={{
                  minWidth: '200px',
                  marginTop: '20px'
                }}
              >
                {isProcessing
                  ? '⏳ Processing...'
                  : `🗑️ Remove Pages from PDF`
                }
              </button>

              {failedCount > 0 && (
                <button
                  className="page-remover-btn page-remover-btn-outline"
                  onClick={() => {
                    analytics.trackButtonClick('retry_processing', 'pdf_page_remover');
                    handleRetryProcessing();
                  }}
                  disabled={isProcessing}
                  style={{
                    marginLeft: '10px'
                  }}
                >
                  🔄 Retry Processing
                </button>
              )}
            </div>

            {/* Progress Section */}
            {(isProcessing || isClearing) && (
              <div className="progress-section" style={{ marginTop: '20px' }}>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: isClearing ? '100%' : `${processingProgress}%`,
                      backgroundColor: isClearing ? '#6c757d' : '#dc3545'
                    }}
                  />
                </div>
                <p className="progress-text">
                  {isClearing
                    ? 'Clearing all data...'
                    : `${Math.round(processingProgress)}% - ${processingStage}`
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        {processingResults.length > 0 && (
          <div className="card">
            <PageRemovalResults
              results={processingResults}
              onClearResults={() => setProcessingResults([])}
              onRetry={handleRetryProcessing}
              isProcessing={isProcessing}
            />
          </div>
        )}

        {/* Footer */}
        <div className="pdf-footer">
          <p>
            <strong>Supported operations:</strong> Remove first/last pages, remove page ranges,
            remove specific pages, or keep only selected pages
          </p>
          <p><small>Maximum file size: 50MB • All processing happens in your browser</small></p>
          <div className="pdf-footer-bottom">
            <p className="pdf-copyright">
              © {new Date().getFullYear()} Utility Tools. All rights reserved.
            </p>
            <p className="pdf-powered-by">
              Powered by nkcodescribbler
            </p>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          className="page-remover-scroll-top"
          onClick={scrollToTop}
          title="Scroll to top"
          aria-label="Scroll to top"
        >
          <span className="page-remover-scroll-icon">↑</span>
        </button>
      )}
    </div>
  );
}

export default PDFPageRemover;